// Excel del Reporte de Clientes con el sistema visual TelCo.
//
// Mismas filas y columnas (mismo orden) que el Excel anterior, con:
// encabezado (período, filtros y generado, sin título), indicadores, bandas de grupo
// sobre los títulos, títulos legibles, importes y fechas como valores reales de
// Excel (se pueden sumar, ordenar y filtrar), anchos según el contenido, filtros,
// filas inmovilizadas, cebra suave y fila de totales. Sin imágenes ni pie.
//
// Unidades que manda /reporte/cliente: los importes en centavos, salvo iva_cft y
// los topes (importe_minimo/maximo y los de Telco), que vienen en pesos. Los
// porcentajes de comisión y las alícuotas, como fracción (0.018 = 1,8 %); los de
// retención, en puntos (2.00 = 2 %).
import ExcelJS from "exceljs";
import saveAs from "file-saver";

const COLOR = {
  cyan: "FF00B0EA",
  lima: "FFCCD400",
  verde: "FF009238",
  gris: "FF2F2F2F",
  gris2: "FF5B6470",
  claro: "FFF5F7F9",
  linea: "FFE8EDF1",
  cyan10: "FFE6F7FD",
  lima10: "FFF8FBD9",
  verde10: "FFE6F4EB",
  blanco: "FFFFFFFF",
};
const FUENTE = "Plus Jakarta Sans";

const FORMATO = {
  importe: '"$" #,##0.00;-"$" #,##0.00',
  fraccion: "0.00%",
  puntos: '0.00" %"',
  coeficiente: "0.0000",
  fecha: "dd/mm/yyyy",
  entero: "0",
};

// Tipos: texto, id (número sin separador), entero, fecha, importe (centavos),
// pesos (ya en pesos), fraccion, puntos, coeficiente.
const GRUPOS = [
  {
    titulo: "Pago y pagador",
    tono: "cyan",
    columnas: [
      ["cliente", "Cliente", "texto"],
      ["cuenta", "Cuenta", "texto"],
      ["rubro", "Rubro", "texto"],
      ["tipo_pago", "Tipo de pago", "texto"],
      ["referencia_externa", "Referencia externa", "texto"],
      ["pago_id", "ID pago", "id"],
      ["estado", "Estado", "texto"],
      ["medio_pago", "Medio de pago", "texto"],
      ["pagador", "Pagador", "texto"],
      ["dni_pagador", "DNI pagador", "texto"],
      ["correo_pagador", "Correo pagador", "texto"],
      ["cuotas", "Cuotas", "entero"],
      ["fecha_pago", "Fecha de pago", "fecha"],
    ],
  },
  {
    titulo: "Liquidación y rendición",
    tono: "lima",
    columnas: [
      ["nro_establecimiento", "N° establecimiento", "texto"],
      ["nro_liquidacion", "N° liquidación", "texto"],
      ["fecha_presentacion", "Fecha de presentación", "fecha"],
      ["fecha_acreditacion", "Fecha de acreditación", "fecha"],
      ["fecha_rendicion", "Fecha de rendición", "fecha"],
      ["banco_referencia", "Referencia banco", "texto"],
    ],
  },
  {
    titulo: "Importes",
    tono: "verde",
    columnas: [
      ["importe_a_pagar", "Importe a pagar", "importe"],
      ["importe_pagado", "Importe pagado", "importe"],
    ],
  },
  {
    titulo: "Retenciones",
    tono: "cyan",
    columnas: [
      ["porcentaje_retencion_iibb", "Retención IIBB (%)", "puntos"],
      ["retencion_iibb", "Retención IIBB", "importe"],
      ["porcentaje_retencion_iva", "Retención IVA (%)", "puntos"],
      ["retencion_iva", "Retención IVA", "importe"],
      ["porcentaje_retencion_ganancias", "Retención Ganancias (%)", "puntos"],
      ["retencion_ganancias", "Retención Ganancias", "importe"],
    ],
  },
  {
    titulo: "Costo financiero",
    tono: "lima",
    columnas: [
      ["alicuota_iva_cft", "Alícuota IVA CFT", "texto"],
      ["cft_coeficiente", "Coeficiente CFT", "coeficiente"],
      ["importe_cft", "Importe CFT", "importe"],
      ["iva_cft", "IVA CFT", "pesos"],
      ["costo_financiero", "Costo financiero", "importe"],
      ["iva_s_costo_financiero", "IVA costo financiero", "importe"],
    ],
  },
  {
    titulo: "Comisión proveedor",
    tono: "verde",
    columnas: [
      ["porcentaje_comision_proveedor", "Comisión proveedor (%)", "fraccion"],
      ["importe_minimo", "Mínimo proveedor", "pesos"],
      ["importe_maximo", "Máximo proveedor", "pesos"],
      ["comision_proveedor", "Comisión proveedor", "importe"],
      ["iva_comision_proveedor", "IVA comisión proveedor", "importe"],
    ],
  },
  {
    titulo: "Comisión Telco",
    tono: "cyan",
    columnas: [
      ["comision_porcentaje_telco", "Comisión Telco (%)", "fraccion"],
      ["importe_minimo_telco", "Mínimo Telco", "pesos"],
      ["importe_maximo_telco", "Máximo Telco", "pesos"],
      ["alicuota_iva", "Alícuota IVA", "fraccion"],
      ["comision_telco", "Comisión Telco", "importe"],
      ["iva_comision_telco", "IVA comisión Telco", "importe"],
    ],
  },
  {
    titulo: "Comisión total",
    tono: "lima",
    columnas: [
      ["comision_total", "Comisión total", "importe"],
      ["iva_comision_total", "IVA comisión total", "importe"],
    ],
  },
  {
    titulo: "Resultado",
    tono: "verde",
    columnas: [
      ["importe_neto_cobrado", "Importe neto cobrado", "importe"],
      ["importe_rendido", "Importe rendido", "importe"],
    ],
  },
  {
    titulo: "Estado y referencias",
    tono: "cyan",
    columnas: [
      ["autorizado", "Autorizado", "fecha"],
      ["revertido", "Revertido", "fecha"],
      ["referencia_bancaria", "Referencia bancaria", "texto"],
      ["transaction_id", "Transaction ID", "texto"],
    ],
  },
  {
    titulo: "Costo de servicio · Neto interno",
    tono: "lima",
    columnas: [
      ["monto_costo_servicio", "Costo de servicio", "importe"],
      ["porcentaje_cargo_servicio", "Costo de servicio (%)", "fraccion"],
      ["importe_neto_cobrado_interno", "Importe neto cobrado interno", "importe"],
    ],
  },
];

export const COLUMNAS_EXCEL = GRUPOS.flatMap((g) =>
  g.columnas.map(([key, titulo, tipo]) => ({ key, titulo, tipo, grupo: g.titulo }))
);

const ES_IMPORTE = (tipo) => tipo === "importe" || tipo === "pesos";

// Celdas que quedan vacías a propósito, con la misma regla que la pantalla.
const VACIAS_SI_ARANCEL_PENDIENTE = new Set([
  "porcentaje_comision_proveedor",
  "comision_proveedor",
  "iva_comision_proveedor",
  "importe_neto_cobrado_interno",
]);

const vacia = (pago, key) =>
  (pago.arancel_pendiente && VACIAS_SI_ARANCEL_PENDIENTE.has(key)) ||
  (pago.sin_neto_operador && key === "importe_neto_cobrado") ||
  (key === "porcentaje_comision_proveedor" && pago.medio_pago === "DEBIN");

// "2026-09-21T…" o "21/09/2026" -> Date en UTC (Excel la guarda sin zona). "" y
// "-" -> null.
export const aFecha = (valor) => {
  if (!valor || valor === "-" || valor === 0) return null;
  const s = String(valor);
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
  return null;
};

const aNumero = (v) => {
  if (v === null || v === undefined || v === "" || v === "-") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const valorCelda = (pago, col) => {
  if (vacia(pago, col.key)) return null;
  const v = pago[col.key];
  switch (col.tipo) {
    case "fecha":
      return aFecha(v);
    case "importe": {
      const n = aNumero(v);
      return n === null ? null : Math.round(n) / 100;
    }
    case "pesos":
    case "puntos":
    case "coeficiente":
    case "entero":
    case "id":
      return aNumero(v);
    case "fraccion": {
      if (col.key === "porcentaje_cargo_servicio") {
        // El backend manda el coeficiente (1.07 = 7 %).
        const n = aNumero(pago.porcentaje_costo_servicio);
        return n > 0 ? Math.round((n - 1) * 1e4) / 1e4 : n;
      }
      return aNumero(v);
    }
    default:
      return v === null || v === undefined || v === "" ? null : String(v);
  }
};

const textoCelda = (valor, tipo) => {
  if (valor === null || valor === undefined) return "";
  if (valor instanceof Date) return "00/00/0000";
  if (ES_IMPORTE(tipo))
    return (valor < 0 ? "-$ " : "$ ") + Math.abs(valor).toLocaleString("es-AR", { minimumFractionDigits: 2 });
  if (tipo === "fraccion" || tipo === "puntos") return "00,00 %";
  return String(valor);
};

// Ancho por tipo y contenido real, con mínimo y máximo para no estirar la hoja.
const LIMITES = {
  texto: [10, 34],
  id: [9, 12],
  entero: [7, 9],
  fecha: [12, 12],
  importe: [13, 18],
  pesos: [12, 16],
  fraccion: [10, 12],
  puntos: [10, 12],
  coeficiente: [10, 12],
};
const anchoColumna = (col, valores) => {
  const [min, max] = LIMITES[col.tipo] || [10, 24];
  // El título va en hasta dos líneas: alcanza con la mitad más larga.
  const palabras = col.titulo.split(" ");
  const mitad = Math.ceil(palabras.length / 2);
  const titulo = Math.max(palabras.slice(0, mitad).join(" ").length, palabras.slice(mitad).join(" ").length);
  let contenido = 0;
  for (const v of valores) contenido = Math.max(contenido, textoCelda(v, col.tipo).length);
  return Math.min(max, Math.max(min, titulo + 2, contenido + 2));
};

const relleno = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
const fuente = (extra = {}) => ({ name: FUENTE, size: 10, color: { argb: COLOR.gris }, ...extra });
const TONOS = {
  cyan: { fondo: COLOR.cyan10, texto: "FF0A6F92" },
  lima: { fondo: COLOR.lima10, texto: "FF6F7400" },
  verde: { fondo: COLOR.verde10, texto: COLOR.verde },
};

// Filas fijas de la hoja: filtros (1-3), acento, indicadores, grupos y títulos.
const FILA = { acento: 4, kpiEtiqueta: 6, kpiValor: 7, grupos: 9, titulos: 10 };

const fechaCorta = (yyyyMmDd) => {
  const [y, m, d] = String(yyyyMmDd).split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Arma el libro. filtros: { fechaDesde, fechaHasta (AAAA-MM-DD), aprobados,
 * cliente, canales, monto, usuario, generado (Date) }.
 */
export function armarLibroReporteClientes({ pagos, totalRendido, filtros }) {
  const cols = COLUMNAS_EXCEL;
  const N = cols.length;
  const filas = pagos.map((p) => cols.map((c) => valorCelda(p, c)));

  const libro = new ExcelJS.Workbook();
  libro.creator = "Wee! Pagos · TelCo";
  libro.created = filtros.generado || new Date();
  const hoja = libro.addWorksheet("Reporte de Clientes", {
    properties: { defaultRowHeight: 18 },
    views: [{ state: "frozen", xSplit: 1, ySplit: FILA.titulos, showGridLines: false }],
    pageSetup: {
      orientation: "landscape",
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.6, header: 0.2, footer: 0.3 },
    },
  });
  hoja.pageSetup.printTitlesRow = `${FILA.titulos}:${FILA.titulos}`;

  cols.forEach((c, i) => {
    hoja.getColumn(i + 1).width = anchoColumna(c, filas.map((f) => f[i]));
  });

  // ── Encabezado: período, filtros y generación (sin título) ─────────────────
  const periodo = `${filtros.aprobados ? "Aprobados" : "Pagos"} del ${fechaCorta(filtros.fechaDesde)} al ${fechaCorta(
    filtros.fechaHasta
  )} · ${pagos.length} filas`;
  const generado = filtros.generado || new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const generadoTexto = `${pad(generado.getDate())}/${pad(generado.getMonth() + 1)}/${generado.getFullYear()} ${pad(
    generado.getHours()
  )}:${pad(generado.getMinutes())}${filtros.usuario ? " · " + filtros.usuario : ""}`;
  const meta = [
    [1, 1, "Período", periodo],
    [1, 5, "Cliente", filtros.cliente || "Todos los clientes"],
    [2, 1, "Canales", filtros.canales || "Todos"],
    [2, 5, "Monto", filtros.monto || "Sin filtro"],
    [3, 1, "Generado", generadoTexto],
  ];
  for (const [fila, col, etiqueta, valor] of meta) {
    hoja.getRow(fila).height = 18;
    const e = hoja.getCell(fila, col);
    e.value = etiqueta.toUpperCase();
    e.font = fuente({ size: 8, bold: true, color: { argb: COLOR.gris2 } });
    e.alignment = { vertical: "middle" };
    const v = hoja.getCell(fila, col + 1);
    v.value = valor;
    v.font = fuente({ bold: true });
    v.alignment = { vertical: "middle" };
    hoja.mergeCells(fila, col + 1, fila, col + 3);
  }

  // Línea de acento: el gradiente TelCo llevado a celdas (mitad cyan, mitad lima).
  const acento = hoja.getRow(FILA.acento);
  acento.height = 4;
  for (let c = 1; c <= N; c++) acento.getCell(c).fill = relleno(c <= Math.ceil(N / 2) ? COLOR.cyan : COLOR.lima);

  // ── Indicadores ──────────────────────────────────────────────────────────
  const sumar = (key) => {
    const i = cols.findIndex((c) => c.key === key);
    return filas.reduce((t, f) => t + (typeof f[i] === "number" ? f[i] : 0), 0);
  };
  const pagosDistintos = new Set(pagos.map((p) => p.pago_id)).size;
  const kpis = [
    ["Pagos", pagosDistintos, FORMATO.entero, COLOR.cyan10],
    ["Importe pagado", sumar("importe_pagado"), FORMATO.importe, COLOR.lima10],
    ["Comisión total", sumar("comision_total"), FORMATO.importe, COLOR.verde10],
    ["Importe rendido", (Number(totalRendido) || 0) / 100, FORMATO.importe, COLOR.claro],
  ];
  hoja.getRow(FILA.kpiEtiqueta).height = 20;
  hoja.getRow(FILA.kpiValor).height = 28;
  kpis.forEach(([etiqueta, valor, fmt, fondo], k) => {
    const desde = 1 + k * 3;
    const hasta = desde + 2;
    for (let c = desde; c <= hasta; c++) {
      hoja.getCell(FILA.kpiEtiqueta, c).fill = relleno(fondo);
      hoja.getCell(FILA.kpiValor, c).fill = relleno(fondo);
    }
    const e = hoja.getCell(FILA.kpiEtiqueta, desde);
    e.value = etiqueta.toUpperCase();
    e.font = fuente({ size: 8, bold: true, color: { argb: COLOR.gris2 } });
    e.alignment = { vertical: "bottom", indent: 1 };
    hoja.mergeCells(FILA.kpiEtiqueta, desde, FILA.kpiEtiqueta, hasta);
    const v = hoja.getCell(FILA.kpiValor, desde);
    v.value = valor;
    v.numFmt = fmt;
    v.font = fuente({ size: 15, bold: true, color: { argb: COLOR.verde } });
    v.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
    hoja.mergeCells(FILA.kpiValor, desde, FILA.kpiValor, hasta);
  });

  // ── Bandas de grupo y títulos ────────────────────────────────────────────
  let c0 = 1;
  hoja.getRow(FILA.grupos).height = 18;
  for (const g of GRUPOS) {
    const c1 = c0 + g.columnas.length - 1;
    const tono = TONOS[g.tono];
    for (let c = c0; c <= c1; c++) {
      const celda = hoja.getCell(FILA.grupos, c);
      celda.fill = relleno(tono.fondo);
      celda.border = { left: c === c0 ? { style: "medium", color: { argb: COLOR.blanco } } : undefined };
    }
    const celda = hoja.getCell(FILA.grupos, c0);
    celda.value = g.titulo.toUpperCase();
    celda.font = fuente({ size: 8, bold: true, color: { argb: tono.texto } });
    celda.alignment = { vertical: "middle", indent: 1 };
    if (c1 > c0) hoja.mergeCells(FILA.grupos, c0, FILA.grupos, c1);
    c0 = c1 + 1;
  }

  const cab = hoja.getRow(FILA.titulos);
  cab.height = 32;
  cols.forEach((c, i) => {
    const celda = cab.getCell(i + 1);
    celda.value = c.titulo;
    celda.fill = relleno(COLOR.cyan);
    celda.font = fuente({ bold: true, color: { argb: COLOR.blanco } });
    celda.alignment = {
      vertical: "middle",
      horizontal: ES_IMPORTE(c.tipo) || ["fraccion", "puntos", "coeficiente", "id", "entero"].includes(c.tipo) ? "right" : "left",
      wrapText: true,
    };
    celda.border = { right: { style: "thin", color: { argb: "FF4CC7F0" } } };
  });

  // ── Datos ────────────────────────────────────────────────────────────────
  const primera = FILA.titulos + 1;
  filas.forEach((valores, r) => {
    const fila = hoja.getRow(primera + r);
    const cebra = r % 2 === 1;
    valores.forEach((valor, i) => {
      const col = cols[i];
      const celda = fila.getCell(i + 1);
      celda.value = valor;
      celda.font = fuente();
      celda.border = { bottom: { style: "thin", color: { argb: COLOR.linea } } };
      if (cebra) celda.fill = relleno(COLOR.claro);
      if (col.tipo === "fecha") celda.numFmt = FORMATO.fecha;
      else if (ES_IMPORTE(col.tipo)) celda.numFmt = FORMATO.importe;
      else if (col.tipo === "fraccion") celda.numFmt = FORMATO.fraccion;
      else if (col.tipo === "puntos") celda.numFmt = FORMATO.puntos;
      else if (col.tipo === "coeficiente") celda.numFmt = FORMATO.coeficiente;
      else if (col.tipo === "id" || col.tipo === "entero") celda.numFmt = FORMATO.entero;
      if (col.tipo === "texto") celda.alignment = { vertical: "middle" };
    });
  });
  const ultima = primera + filas.length - 1;
  hoja.autoFilter = { from: { row: FILA.titulos, column: 1 }, to: { row: FILA.titulos, column: N } };

  // ── Totales (SUBTOTAL: respeta los filtros que aplique el usuario) ────────
  const filaTot = hoja.getRow(ultima + 1);
  filaTot.height = 22;
  cols.forEach((c, i) => {
    const celda = filaTot.getCell(i + 1);
    celda.fill = relleno(COLOR.lima10);
    celda.font = fuente({ bold: true });
    celda.border = { top: { style: "medium", color: { argb: COLOR.verde } } };
    if (c.tipo === "importe" && filas.length > 0) {
      const letra = hoja.getColumn(i + 1).letter;
      const total = filas.reduce((t, f) => t + (typeof f[i] === "number" ? f[i] : 0), 0);
      celda.value = { formula: `SUBTOTAL(109,${letra}${primera}:${letra}${ultima})`, result: Math.round(total * 100) / 100 };
      celda.numFmt = FORMATO.importe;
    }
  });
  filaTot.getCell(1).value = "Totales (filas visibles)";

  return libro;
}

export async function descargarExcelReporteClientes(opciones) {
  const libro = armarLibroReporteClientes(opciones);
  const buffer = await libro.xlsx.writeBuffer();
  const { fechaDesde, fechaHasta } = opciones.filtros;
  const nombre = `Reporte de Clientes ${fechaCorta(fechaDesde).replace(/\//g, "-")} al ${fechaCorta(fechaHasta).replace(
    /\//g,
    "-"
  )}.xlsx`;
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), nombre);
}
